/**
 * Theme cart glue.
 *
 * - "?viewcart=true" opens the drawer; /cart is sent there (layout/theme.liquid).
 * - Relewise recommendations in the drawer: PurchasedWithMultipleProducts for the cart lines, with
 *   product id AND variant id (SKU) per line and the cart's products excluded by product id (Relewise
 *   go-live check 2026-09-15); popular products when the cart is empty. Fetched when the drawer
 *   opens and again after cart changes.
 * - A recommendation is added through the theme's own add-to-cart path (theme:cart:add), so errors,
 *   the cart count and the drawer refresh behave as for every other add.
 * - Jersey-builder add-ons (print, sleeve badge) share an _attribution id with their jersey; when the
 *   jersey leaves the cart its add-ons go too.
 */
(() => {
  const drawer = document.querySelector('cart-drawer.agf-cart');
  if (!drawer) return;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const lists = {
    cart: drawer.querySelector('[data-relewise-cart-recommendations="cart"]'),
    empty: drawer.querySelector('[data-relewise-cart-recommendations="empty"]'),
  };
  const RECOMMENDATION_POPULAR_DAYS = 14;
  const REFRESH_DELAY_MS = 300;

  const open = () => drawer.dispatchEvent(new CustomEvent('theme:cart-drawer:show'));
  const isOpen = () => drawer.classList.contains('is-open');

  /* ---- Relewise recommendations ------------------------------------------------------- */
  const lines = () => [...drawer.querySelectorAll('[data-items-holder] [data-item]')]
    .filter((item) => item.dataset.productId)
    .map((item) => ({ productId: item.dataset.productId, variantId: item.dataset.sku || undefined }));

  let shownFor = null;
  let sequence = 0;

  async function refresh() {
    // Recommendations switched off (Theme settings → Relewise): the drawer has no recommendation lists.
    if (!lists.cart || !lists.empty) return;
    const core = window.agf && window.agf.relewiseCore;
    const cartLines = lines();
    const empty = cartLines.length === 0;
    const target = empty ? lists.empty : lists.cart;
    (empty ? lists.cart : lists.empty).hidden = true;

    const key = empty ? 'empty' : cartLines.map((line) => `${line.productId}:${line.variantId || ''}`).sort().join('|');
    if (key === shownFor) {
      target.hidden = !target.querySelector('.agf-upsell');
      return;
    }
    if (!core || !core.ready()) {
      target.hidden = true;
      return;
    }

    const run = ++sequence;
    try {
      const { Relewise } = window;
      const limit = Number(target.dataset.limit) || 2;
      const settings = core.settings(empty ? core.LOCATIONS.emptyCart : core.LOCATIONS.cart, await core.user());
      const builder = empty
        ? new Relewise.PopularProductsBuilder(settings).basedOn('MostPurchased').sinceMinutesAgo(RECOMMENDATION_POPULAR_DAYS * 24 * 60)
        : new Relewise.PurchasedWithMultipleProductsBuilder(settings).addProducts(cartLines);
      builder
        // A couple extra so a card Shopify won't render (Locksmith, sold out since sync) doesn't leave a gap.
        .setNumberOfRecommendations(limit + 2)
        .setSelectedProductProperties({ dataKeys: [core.HANDLE_KEY] })
        .filters((filters) => {
          core.recommendationFilters(filters);
          if (!empty) filters.addProductIdFilter(cartLines.map((line) => line.productId), true);
          // Adult match jerseys (tag exclude_rebuy) are bought through the jersey builder, never from the cart.
          filters.addProductDataFilter('Tags', (c) => c.addContainsCondition(Relewise.DataValueFactory.string('exclude_rebuy'), 'Any', true), true, false);
        });

      const response = await core.recommend(builder.build());
      const cards = (await core.cards(core.handles(response && response.recommendations), { view: 'relewise-cart-card' })).slice(0, limit);
      if (run !== sequence) return;

      target.querySelector('[data-relewise-cart-list]').innerHTML = cards.map((card) => card.html).join('');
      target.hidden = cards.length === 0;
      shownFor = key;
    } catch (error) {
      if (run === sequence) target.hidden = true;
    }
  }

  let refreshTimer = null;
  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refresh, REFRESH_DELAY_MS);
  }

  document.addEventListener('theme:cart-drawer:open', refresh);
  document.addEventListener('theme:cart:change', () => {
    removeOrphanAddons();
    if (isOpen()) scheduleRefresh();
    else shownFor = null;
  });

  /* ---- Recommendation actions --------------------------------------------------------- */
  drawer.addEventListener('click', (event) => {
    const choose = event.target.closest('[data-upsell-choose]');
    if (choose) {
      event.preventDefault();
      const form = choose.closest('form');
      choose.hidden = true;
      form.querySelector('.agf-upsell__options').hidden = false;
      form.querySelector('[data-upsell-add]').hidden = false;
      return;
    }

    const add = event.target.closest('[data-upsell-add]');
    if (add) {
      event.preventDefault();
      document.dispatchEvent(new CustomEvent('theme:cart:add', { bubbles: true, detail: { button: add } }));
    }
  });

  /* ---- Jersey-builder add-ons ------------------------------------------------------------ */
  let removing = false;

  async function removeOrphanAddons() {
    if (removing) return;
    const items = [...drawer.querySelectorAll('[data-items-holder] [data-attribution]')];
    const jerseys = new Set(items.filter((item) => item.dataset.attributionType === 'main').map((item) => item.dataset.attribution));
    const orphans = items.filter((item) => item.dataset.attributionType === 'addon' && !jerseys.has(item.dataset.attribution));
    if (!orphans.length) return;

    removing = true;
    try {
      await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: Object.fromEntries(orphans.map((item) => [item.dataset.item, 0])) }),
      });
      const cartItems = drawer.querySelector('cart-items');
      if (cartItems && typeof cartItems.getCart === 'function') cartItems.getCart();
    } finally {
      removing = false;
    }
  }

  /* ---- ?viewcart=true (last, so the open listener above catches it) ------------------- */
  const url = new URL(window.location.href);
  if (url.searchParams.get('viewcart') === 'true') {
    url.searchParams.delete('viewcart');
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    open();
  }
})();
